import { useEffect } from "react";
import { AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { postRequest } from "helpers/axios";
import { API_ROUTES } from "helpers/constants/apis";
import { setInfo } from "../redux/slices/basicInfoReducer";
import { setUserData } from "../redux/slices/externalUserReducer";
import { setAllPreviousSignatures } from "../redux/slices/signatureReducer";
import { fetchIpInfo } from "../utils/fetchIpInfo";

interface UseAppInitializerProps {
  setIsAlreadySign: (val: boolean) => void;
  setUserErrorMsg: (val: string) => void;
  setIsLoading: (val: boolean) => void;
  fetchingCordinates: (tiUUID: string, signatoryUUID: string) => Promise<void>;
  uploadPdf: (uuid: string, tiUUID: string | null) => Promise<void>;
}

export const useAppInitializer = ({
  setIsAlreadySign,
  setUserErrorMsg,
  setIsLoading,
  fetchingCordinates,
  uploadPdf,
}: UseAppInitializerProps) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const fetchingUsersResources = async (uuidSignatory: string) => {
    console.log('fetchingUsersResources called with uuidSignatory:', uuidSignatory);
    try {
      const {
        data: { data: responseData },
      }: AxiosResponse = await postRequest(
        API_ROUTES.COMMON_EXTERNALUSER_DATA_CHECKORADD,
        false,
        { uuidSignatory }
      );
      console.log('responseData from fetchingUsersResources:', responseData);
      const userId = responseData.data.userId;
      console.log('userId before fetchAll1:', userId);
      const {
        data: { data: signatureData },
      }: AxiosResponse = await postRequest(
        API_ROUTES.COMMON_EXTERNALUSER_SIGNATURE_FETCHALL,
        false,
        { userId }
      );

      dispatch(setUserData({ userId }));
      dispatch(setAllPreviousSignatures({ allPreviousSignatures: signatureData }));
    } catch (err: any) {
      // continue loading
    }
  };

  const trackDocumentViewed = async (
    uuidTemplateInstance: string,
    signatoryUniqUUID: string
  ) => {
    
    try {
      const locationData: any = await fetchIpInfo();
      await postRequest(API_ROUTES.AUDIT_TRACKDOCUMENTVIEWED, false, {
        docSignRequestUUID: uuidTemplateInstance,
        signatoryUUID: signatoryUniqUUID,
        location: locationData,
        action: "VIEWED"
      });
    } catch (err) {
      // Audit tracking errors are non-fatal
    }
  };

  const checkStatus = async (
    signatoryUniqUUID: string,
    uuidTemplateInstance: string
  ) => {
    try {
      console.log("Checking document status for signatory:", signatoryUniqUUID,);
      const {
        data: { data: responseData },
      }: AxiosResponse = await postRequest(API_ROUTES.CHECKSTATUS, false, {
        uuid_signatory: signatoryUniqUUID,
        id: uuidTemplateInstance,
      });

      console.log("Status check responseData:", responseData);
      const expiryDate = responseData?.expiryDate;
      if (expiryDate && new Date() > new Date(expiryDate)) {
        setUserErrorMsg("Sorry, the document has expired.");
        setIsAlreadySign(true);
        localStorage.clear();
        return;
      }

      await trackDocumentViewed(uuidTemplateInstance, signatoryUniqUUID);
    } catch (err: any) 
    {
      // console.log("Error during status check:", err.response);
      const msg = err.response?.data?.message?.toLowerCase();
      console.log("Error during status check:", msg);
      if (msg === "sorry your signature is already done") {
        setUserErrorMsg("Sorry You Had Already Done Your Work");
        setIsAlreadySign(true);
      } else if (msg === "sorry your template is already signed") {
        setUserErrorMsg(
          "Sorry Your Template Is Already Signed Either By You Or By SomeOne Else In Your Group"
        );
        setIsAlreadySign(true);
      } else if (msg === "sorry someone had rejected this template") {
        setUserErrorMsg(
          "Sorry one of the signatory had rejected this template so you are unable to go further"
        );
        setIsAlreadySign(true);
      }
      if (err.response) {
        localStorage.clear();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // On mount: validate URL params and kick off the signing flow
  useEffect(() => {
    const init = async () => {
      const uuid = searchParams.get("id");
      const id = searchParams.get("docSignRequestUUID");
      const uuidSignatory = searchParams.get("signatoryUUID");
      const salesforceOrgId = searchParams.get("orgId");
      console.log("Salesforce Org ID from URL params:", salesforceOrgId);
      console.log("Initializing app with params:", { uuid, id, uuidSignatory });
      if (!uuid || !id || !uuidSignatory) {
        navigate("/page404", { replace: true });
        return;
      }

      dispatch(setInfo({ uuid, uuidTemplateInstance: id, uuidSignatory,salesforceOrgId  }));
      await checkStatus(uuidSignatory, id);
    };
    init();
    // }, []);
  }, [searchParams, navigate]);


  // Fetch PDF and coordinate data on mount
  useEffect(() => {
    const fetchingAsync = async () => {
      const uuid = searchParams.get("id");
      const uuidTemplateInstance = searchParams.get("docSignRequestUUID");
      const uuidSignatory = searchParams.get("signatoryUUID");
      console.log("@@@@@");
      if (!uuidSignatory || !uuidTemplateInstance || !uuid) return;

      await fetchingUsersResources(uuidSignatory);
      await fetchingCordinates(uuidTemplateInstance, uuidSignatory);
      await uploadPdf(uuid, uuidTemplateInstance);
    };
    fetchingAsync();
  }, []);

  return {};
};
